import { EventDateLink } from "@/components/artifacts/shared/EventDateLink.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Calendar, Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type DateTimeRangeComponentData = Extract<ArtifactComponent, { type: "dateTimeRange" }>;

const dateToLocalInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const localInputValueToMs = (value: string): number => new Date(value).getTime();

// Client-side fallback formatter, used only when the server didn't supply a
// pre-formatted `displayText`. It renders in the device zone, so the server's
// `displayText` (converted in the account's effective zone) is always
// preferred for the read view (BIG-4825).
const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatTime = (date: Date) =>
  date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

const formatTimeZone = (date: Date) =>
  date.toLocaleDateString("en-US", { timeZoneName: "short" }).split(" ").pop();

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

// Timed-range fallback for when the server omits `displayText`. An event
// spanning more than one day carries its end date so a multi-day hold isn't
// mistaken for a single long day (BIG-6240). It has no all-day branch — the
// server always supplies `displayText` for all-day events.
const formatRange = (start: number, end: number) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startPart = `${formatDate(startDate)} ${formatTime(startDate)}`;
  const endPart = sameDay(startDate, endDate)
    ? formatTime(endDate)
    : `${formatDate(endDate)} ${formatTime(endDate)}`;
  return `${startPart} - ${endPart} ${formatTimeZone(startDate)}`;
};

export const DateTimeRangeBlock = ({ component }: { component: DateTimeRangeComponentData }) => {
  const { isReadOnly, patch } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;

  const [editing, setEditing] = useState(false);
  const [startMs, setStartMs] = useState<number | undefined>(component.startMs);
  const [endMs, setEndMs] = useState<number | undefined>(component.endMs);

  useEffect(() => {
    setStartMs(component.startMs);
    setEndMs(component.endMs);
  }, [component.startMs, component.endMs]);

  const save = useCallback(async () => {
    if (startMs === undefined || endMs === undefined) return;
    if (startMs >= endMs) {
      errorToast("End time must be after start time");
      return;
    }
    try {
      await patch([
        { path: component.startEditPath, value: startMs },
        { path: component.endEditPath, value: endMs },
      ]);
      setEditing(false);
    } catch (error) {
      console.error(error);
    }
  }, [startMs, endMs, patch, component.startEditPath, component.endEditPath]);

  const cancel = useCallback(() => {
    setStartMs(component.startMs);
    setEndMs(component.endMs);
    setEditing(false);
  }, [component.startMs, component.endMs]);

  return (
    <div className="flex flex-row gap-2 items-start">
      <Calendar className="size-5 mt-0.5" />
      <div className="flex flex-col gap-2 flex-1">
        {editing ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 items-center">
              <span className="text-sm text-muted-foreground w-12">Start:</span>
              <Input
                type="datetime-local"
                value={startMs ? dateToLocalInputValue(new Date(startMs)) : ""}
                onChange={(e) =>
                  setStartMs(e.target.value ? localInputValueToMs(e.target.value) : undefined)
                }
                className="h-8 text-sm flex-1"
              />
            </div>
            <div className="flex flex-row gap-2 items-center">
              <span className="text-sm text-muted-foreground w-12">End:</span>
              <Input
                type="datetime-local"
                value={endMs ? dateToLocalInputValue(new Date(endMs)) : ""}
                onChange={(e) =>
                  setEndMs(e.target.value ? localInputValueToMs(e.target.value) : undefined)
                }
                className="h-8 text-sm flex-1"
              />
            </div>
            <div className="flex flex-row gap-2">
              <Button
                onClick={save}
                variant="outlinePopover"
                size="action"
                className="h-6 text-xs px-2 py-1 font-medium"
                data-statsig-name="artifact-view-save-datetime"
              >
                Save
              </Button>
              <Button
                onClick={cancel}
                variant="ghost"
                size="action"
                className="h-6 text-xs px-2 py-1 font-medium"
                data-statsig-name="artifact-view-cancel-datetime"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2">
            {startMs && endMs && (
              <EventDateLink
                startDateMs={startMs}
                endDateMs={endMs}
                className="text-sm @md:text-base text-foreground"
              >
                {component.displayText ?? formatRange(startMs, endMs)}
              </EventDateLink>
            )}
            {canEdit ? (
              <Button
                onClick={() => setEditing(true)}
                variant="ghost"
                size="action"
                className="h-6 w-6 p-0"
                aria-label="Edit date and time"
                data-statsig-name="artifact-view-edit-datetime"
              >
                <Pencil className="size-3" />
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
